import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys, creditMemoKeys, invoiceKeys } from '@/lib/queryKeys';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { billingV2Post } from '@/lib/api/clients/billing-v2';
import type { CreateCreditMemoInput } from '@/types/creditMemo';

interface CreateCreditMemoResult {
  id: string;
  status: string;
  creditMemoNumber: string;
}

export function useCreateCreditMemo() {
  const queryClient = useQueryClient();

  return useApiMutation<CreateCreditMemoResult, CreateCreditMemoInput>({
    mutationFn: (input) =>
      billingV2Post<CreateCreditMemoResult, CreateCreditMemoInput>(
        billingV2Endpoints.creditMemoCreate(),
        input,
      ),
    onSuccess: (result, input) => {
      // A new memo adds to the customer's available credit and to the
      // "Apply memo" suggestions on their open invoices, not only the lists.
      void queryClient.invalidateQueries({ queryKey: creditMemoKeys.all() });
      if (input.contactId) {
        void queryClient.invalidateQueries({ queryKey: contactKeys.detail(input.contactId) });
        void queryClient.invalidateQueries({
          queryKey: contactKeys.financialActivityBase(input.contactId),
        });
      }
      if (input.invoiceId) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(input.invoiceId) });
      }
      toast.success(
        result.status === 'PENDING_APPROVAL'
          ? `Credit memo ${result.creditMemoNumber} created — pending finance approval`
          : `Credit memo ${result.creditMemoNumber} created`,
      );
    },
  });
}
